import type { ApiErrorKind, ClassifiedApiError } from './classifyApiError';

export interface ErrorToastContent {
  title: string;
  description: string;
  variant: 'destructive';
  // Index signature so this satisfies `@ais-ui/ui-core`'s `Toast` prop type
  // (`ToasterToast` allows arbitrary extra props) without this module having
  // to import anything from `ui-core` — it stays a plain, dependency-free
  // data shape that any toast implementation could consume.
  [key: string]: unknown;
}

/**
 * Hardcoded English copy — intentionally *not* routed through
 * `@ais-ui/locale-management`, matching the existing precedent set by the
 * websocket stack's toasts (`useReconnect`, `useGracefulLogout`), and
 * required because `maritime-control-center`/`data-forge` don't use the
 * localization system at all. `mutateCallbacks` (ais mutations) remains the
 * one place that shows localized copy, for its own entity/mutation-specific
 * messages.
 */
const COPY_BY_KIND: Record<
  ApiErrorKind,
  { title: string; description: string }
> = {
  'bad-request': {
    title: 'Bad request',
    description:
      'The request was invalid. Please check your input and try again.',
  },
  unauthorized: {
    title: 'Not authorized',
    description: 'You may need to log in again to continue.',
  },
  forbidden: {
    title: 'Access denied',
    description: "You don't have permission to do that.",
  },
  'not-found': {
    title: 'Not found',
    description: 'The requested resource could not be found.',
  },
  conflict: {
    title: 'Conflict',
    description:
      'The resource may have been modified elsewhere. Please refresh and try again.',
  },
  validation: {
    title: 'Invalid data',
    description: 'The data provided is invalid. Please check your input.',
  },
  'rate-limited': {
    title: 'Too many requests',
    description: 'Please wait a moment before trying again.',
  },
  server: {
    title: 'Server error',
    description: 'A server error occurred. Please try again later.',
  },
  network: {
    title: 'Connection problem',
    description:
      'Could not reach the server. Check your connection and try again.',
  },
  // Not actually toasted by `createAppQueryClient` — the service-availability
  // circuit breaker owns the UX for these (consumers deliberately swallow
  // `ServiceUnavailableError`). Copy exists only to keep the map exhaustive.
  'service-unavailable': {
    title: 'Service unavailable',
    description: 'This service is currently unavailable.',
  },
  // Not toasted by `createAppQueryClient` either — the websocket reconnect /
  // grace-period flow owns the UX for a dead session, and a toast per failing
  // query would spam the user while it recovers. Copy exists only to keep the
  // map exhaustive.
  session: {
    title: 'Session unavailable',
    description: 'Reconnecting to the server. Please wait a moment.',
  },
  unknown: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again.',
  },
};

/** Maps a classified error to the toast props the shared `useToast` expects. */
export function getErrorToastContent(
  classified: ClassifiedApiError,
): ErrorToastContent {
  return { ...COPY_BY_KIND[classified.kind], variant: 'destructive' };
}
